import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, X, PencilSimple, Trash, Sparkle, UploadSimple, Robot, CheckCircle, GlobeHemisphereWest } from "@phosphor-icons/react";

const CATEGORIES = ["History", "Science", "Sports", "Entertainment", "Technology", "Politics", "Africa", "General Knowledge"];
const DIFFICULTIES = ["easy", "medium", "hard", "expert"];
const STATUSES = ["pending", "approved", "rejected", "archived"];

export default function Questions() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterDiff, setFilterDiff] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null); // null | "new" | <question>
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleOne = (qid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(qid)) next.delete(qid); else next.add(qid);
    return next;
  });
  const selectAllVisible = () => setSelected(new Set(rows.map((r) => r.qid)));
  const clearSelection = () => setSelected(new Set());

  const bulkSetStatus = async (status) => {
    if (bulkBusy || selected.size === 0) return;
    if (!window.confirm(`Set status='${status}' on ${selected.size} question${selected.size === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/questions/bulk-status", {
        qids: Array.from(selected), status,
      });
      toast.success(`${data.modified} question${data.modified === 1 ? "" : "s"} → ${status}`);
      clearSelection();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk update failed"); }
    finally { setBulkBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/questions", { params: {
        q: q || undefined,
        category: filterCat || undefined,
        difficulty: filterDiff || undefined,
        status: filterStatus || undefined,
      }});
      setRows(data.questions || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterDiff, filterStatus]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  // Listen for cross-component "jump to pending" clicks from the AI panel counter
  useEffect(() => {
    const handler = (e) => setFilterStatus(e.detail || "pending");
    window.addEventListener("questions:filter", handler);
    return () => window.removeEventListener("questions:filter", handler);
  }, []);

  const save = async (payload) => {
    try {
      if (payload.qid) {
        await api.put(`/admin/questions/${payload.qid}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Question created");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const archive = async (qid) => {
    if (!confirm("Archive this question? It won't be served to players.")) return;
    try {
      await api.delete(`/admin/questions/${qid}`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <header className="hidden lg:flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] text-white/40 uppercase tracking-[0.18em]">Command Center</p>
          <h1 className="font-display text-3xl font-medium tracking-tight">Question Bank</h1>
          <p className="text-sm text-white/60 mt-2">{total.toLocaleString()} questions in the bank · the engine of every mode.</p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && <AutoTagAfricanBtn onDone={load} />}
          <button
            data-testid="admin-question-new-btn"
            onClick={() => setEditing("new")}
            className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm hover:bg-nk-accentSoft inline-flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> New question
          </button>
        </div>
      </header>

      <div className="lg:hidden">
        <button onClick={() => setEditing("new")} className="w-full px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center justify-center gap-2">
          <Plus size={14} weight="bold" /> New question
        </button>
      </div>

      <AIPanel onChange={load} />

      <TaxonomyStrip />

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 bg-nk-bg border border-nk-line rounded-sm px-3 py-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-white/60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search question text…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterDiff} onChange={(e) => setFilterDiff(e.target.value)} className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm capitalize">
          <option value="">Any difficulty</option>
          {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm capitalize">
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {rows.length > 0 && (
          <div className="flex items-center gap-2 text-[11px] text-white/60">
            <button onClick={selectAllVisible}
                    className="px-2 py-1 border border-nk-line rounded-sm hover:text-nk-text hover:border-nk-gold"
                    data-testid="questions-select-all">
              Select all {rows.length}
            </button>
            {selected.size > 0 && (
              <button onClick={clearSelection}
                      className="px-2 py-1 text-white/60 hover:text-nk-text">
                Clear ({selected.size})
              </button>
            )}
          </div>
        )}

        {selected.size > 0 && (
          <div className="nk-card p-3 border-l-2 border-nk-gold flex flex-wrap items-center gap-2 sticky top-16 z-20 bg-nk-surface/95 backdrop-blur-sm"
               data-testid="questions-bulk-toolbar">
            <p className="text-sm font-medium">
              <span className="text-nk-gold font-mono">{selected.size}</span> selected
            </p>
            <div className="ml-auto flex items-center gap-1.5">
              <button onClick={() => bulkSetStatus("approved")} disabled={bulkBusy}
                      data-testid="questions-bulk-approve"
                      className="text-xs px-3 py-1.5 bg-nk-gold text-nk-bg font-medium rounded-sm hover:bg-nk-gold/90 disabled:opacity-40">
                Approve {selected.size}
              </button>
              <button onClick={() => bulkSetStatus("rejected")} disabled={bulkBusy}
                      data-testid="questions-bulk-reject"
                      className="text-xs px-3 py-1.5 border border-nk-bad/60 text-nk-bad rounded-sm hover:bg-nk-bad/10 disabled:opacity-40">
                Reject {selected.size}
              </button>
              <button onClick={() => bulkSetStatus("archived")} disabled={bulkBusy}
                      data-testid="questions-bulk-archive"
                      className="text-xs px-3 py-1.5 border border-nk-line text-white/60 hover:text-nk-text rounded-sm disabled:opacity-40">
                Archive {selected.size}
              </button>
            </div>
          </div>
        )}

        {rows.map((r) => (
          <QuestionRow key={r.qid} q={r} onEdit={() => setEditing(r)} onArchive={() => archive(r.qid)}
                       selected={selected.has(r.qid)} onToggleSelect={() => toggleOne(r.qid)} />
        ))}
        {rows.length === 0 && (
          <div className="nk-card p-10 text-center text-white/60 text-sm">No questions match.</div>
        )}
      </div>

      {editing && (
        <QuestionEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function QuestionRow({ q, onEdit, onArchive, selected, onToggleSelect }) {
  return (
    <div className={`nk-card p-4 transition-colors ${selected ? "border-nk-gold bg-nk-gold/5" : ""}`}>
      <div className="flex items-start gap-3">
        <input type="checkbox"
               checked={!!selected}
               onChange={onToggleSelect}
               aria-label="Select question"
               data-testid={`question-check-${q.qid}`}
               className="mt-1 shrink-0 accent-nk-gold" />
        <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="font-display text-base leading-snug flex-1">{q.body}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={onEdit} className="p-1.5 text-white/60 hover:text-nk-text" title="Edit"><PencilSimple size={14} /></button>
          <button onClick={onArchive} className="p-1.5 text-white/60 hover:text-nk-bad" title="Archive"><Trash size={14} /></button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/60 font-mono uppercase tracking-wider">
        <span>{q.category}</span>
        <span className="text-white/40">·</span>
        <span className="capitalize">{q.difficulty}</span>
        <span className="text-white/40">·</span>
        <span className={statusClass(q.status)}>{q.status || "approved"}</span>
        <span className="text-white/40">·</span>
        <span>{q.country_tag || "ALL"}</span>
      </div>
        </div>
      </div>
    </div>
  );
}

function statusClass(s) {
  if (s === "live") return "text-nk-gold";
  if (s === "draft") return "text-nk-warn";
  if (s === "archived") return "text-white/40 line-through";
  return "text-nk-text";
}

function QuestionEditor({ initial, onClose, onSave }) {
  const [body, setBody] = useState(initial?.body || "");
  const [options, setOptions] = useState(initial?.options || ["", "", "", ""]);
  const [answerIndex, setAnswerIndex] = useState(initial?.answer_index ?? 0);
  const [category, setCategory] = useState(initial?.category || "History");
  const [difficulty, setDifficulty] = useState(initial?.difficulty || "easy");
  const [countryTag, setCountryTag] = useState(initial?.country_tag || "ALL");
  const [status, setStatus] = useState(initial?.status || "approved");
  const [explanation, setExplanation] = useState(initial?.explanation || "");

  const submit = () => {
    if (!body.trim()) return toast.error("Question body required");
    if (options.some((o) => !o.trim())) return toast.error("All 4 options required");
    onSave({
      qid: initial?.qid,
      body: body.trim(),
      options: options.map((o) => o.trim()),
      answer_index: answerIndex,
      category, difficulty, country_tag: countryTag, status,
      explanation: explanation.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-medium">{initial ? "Edit question" : "New question"}</h2>
          <button onClick={onClose} className="text-white/60 hover:text-nk-text"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <Field label="Question">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
              className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none resize-none" />
          </Field>

          <div>
            <p className="text-[11px] text-white/60 uppercase tracking-wider mb-2">Options · pick the correct one</p>
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setAnswerIndex(i)}
                  className={`w-6 h-6 shrink-0 rounded-sm border text-xs font-mono flex items-center justify-center transition-colors ${
                    answerIndex === i ? "border-nk-gold bg-nk-gold text-nk-bg" : "border-nk-line text-white/60"
                  }`}
                  title="Mark correct"
                >{String.fromCharCode(65 + i)}</button>
                <input
                  value={opt}
                  onChange={(e) => { const copy = [...options]; copy[i] = e.target.value; setOptions(copy); }}
                  placeholder={`Option ${String.fromCharCode(65 + i)}`}
                  className="flex-1 bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Difficulty">
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm capitalize">
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Country tag">
              <select value={countryTag} onChange={(e) => setCountryTag(e.target.value)} className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm">
                <option value="ALL">All</option>
                <option value="NG">Nigeria-specific</option>
                <option value="GH">Ghana-specific</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm capitalize">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Explanation (optional)">
            <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={2}
              placeholder="Shown to players after the reveal."
              className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none resize-none" />
          </Field>

          <div className="flex gap-2 pt-2">
            <button onClick={submit} data-testid="admin-question-save-btn" className="flex-1 px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm hover:bg-nk-accentSoft">
              {initial ? "Save changes" : "Create question"}
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-nk-line text-sm text-white/60 rounded-sm hover:text-nk-text">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] text-white/60 uppercase tracking-wider mb-1">{label}</p>
      {children}
    </div>
  );
}

// ============================================================
// AI Tools panel — manual generate + file upload + daily auto status
// ============================================================
function AIPanel({ onChange }) {
  const fileRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(20);
  const [champOpen, setChampOpen] = useState(false);
  const [dedupe, setDedupe] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [useHeatmap, setUseHeatmap] = useState(false);
  const [heatmap, setHeatmap] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const refresh = async () => {
    try { const { data } = await api.get("/admin/questions/ai/status"); setStatus(data); }
    catch (_) { /* might be missing permission */ }
  };
  const refreshHeatmap = async () => {
    try { const { data } = await api.get("/admin/questions/heatmap"); setHeatmap(data); }
    catch (_) { /* ignore */ }
  };
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await api.get("/admin/questions/ai/status"); if (alive) setStatus(data); } catch (_) { /* ignore */ }
      try { const { data } = await api.get("/admin/questions/heatmap"); if (alive) setHeatmap(data); } catch (_) { /* ignore */ }
    })();
    const id = setInterval(() => { refresh(); }, 12000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!status) return null;

  const generateNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/questions/ai/generate-now", {
        count,
        custom_prompt: customPrompt.trim() || null,
        use_heatmap: useHeatmap,
      });
      toast.success(`AI inserted ${data.inserted} new pending questions`);
      onChange?.();
      refresh();
      refreshHeatmap();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/questions/ai/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`AI extracted ${data.inserted} questions from "${file.name}"`);
      onChange?.();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extraction failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportCSV = () => {
    const token = localStorage.getItem("nk_session_token");
    const url = `${api.defaults.baseURL}/admin/questions/export?format=csv`;
    // Use authenticated fetch + blob download since axios baseUrl has /api
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `nkognito_questions_${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(u);
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };
  const exportJSON = () => {
    const token = localStorage.getItem("nk_session_token");
    fetch(`${api.defaults.baseURL}/admin/questions/export?format=json`,
          { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `nkognito_questions_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(u);
        toast.success("JSON downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  const runDedupe = async () => {
    if (busy) return;
    setBusy(true);
    const toastId = toast.loading("Starting dedupe job…");
    try {
      // Kick off — returns immediately with a job_id
      const { data: start } = await api.post("/admin/questions/ai/dedupe");
      const jobId = start.job_id;
      if (start.reused) {
        toast.info("A dedupe job is already running — attaching to it.", { id: toastId });
      }
      // Poll every 2s until done/failed
      let attempts = 0;
      const maxAttempts = 300;   // ~10 min safety cap
      let final = null;
      while (attempts++ < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: st } = await api.get(`/admin/questions/ai/dedupe/status/${jobId}`);
        const p = st.progress || {};
        toast.loading(
          p.chunks_total
            ? `Dedupe · chunk ${p.chunks_done}/${p.chunks_total} · ${p.groups_found} groups`
            : "Dedupe running…",
          { id: toastId },
        );
        if (st.status === "done" || st.status === "failed") { final = st; break; }
      }
      if (!final) throw new Error("Dedupe timed out. Try again.");
      if (final.status === "failed") {
        toast.error(final.error || "Dedupe failed", { id: toastId });
        return;
      }
      const groups = final.groups || [];
      const scanned = final.progress?.scanned || 0;
      setDedupe({ groups, scanned, budget_exceeded: final.budget_exceeded });
      if (groups.length === 0) toast.success(`Scanned ${scanned} — no duplicates found 🎉`, { id: toastId });
      else toast.success(`Found ${groups.length} duplicate group(s) across ${scanned} questions`, { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Dedupe failed", { id: toastId });
    } finally { setBusy(false); }
  };

  const lastDone = (status.last_runs || []).find((r) => r.status === "done");

  return (
    <section className="nk-card p-4 border-l-2 border-nk-gold" data-testid="ai-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="eyebrow text-nk-gold flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> AI Question Pipeline
          </p>
          <p className="text-sm text-white/60 mt-0.5">
            Gemini fills the bank automatically every 24h. You can also generate a batch on demand
            or upload a study file and let AI extract the questions.
          </p>
        </div>
        <div className="flex gap-4 text-xs">
          <button onClick={() => { const ev = new CustomEvent("questions:filter", { detail: "pending" }); window.dispatchEvent(ev); }}
                  data-testid="ai-jump-pending"
                  className="hover:text-nk-text transition-colors">
            <span className="text-white/60">Pending:</span> <span className="font-mono text-nk-warn">{status.pending_count}</span>
          </button>
          <span><span className="text-white/60">Approved:</span> <span className="font-mono text-nk-gold">{status.approved_count}</span></span>
          <span><span className="text-white/60">Auto today:</span> <span className="font-mono">{status.auto_added_today}</span></span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 bg-nk-bg border border-nk-line rounded-sm px-2 py-1">
          <label className="text-[10px] text-white/60 uppercase tracking-wider">Batch</label>
          <input type="number" min={1} max={50} value={count}
                 onChange={(e) => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 20)))}
                 data-testid="ai-batch-count"
                 className="w-12 bg-transparent text-xs text-center outline-none" />
        </div>
        <button onClick={generateNow} disabled={busy} data-testid="ai-generate-now"
                className="px-3 py-1.5 bg-nk-gold text-nk-bg text-xs font-medium rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
          <Robot size={12} weight="duotone" /> {busy ? "Working…" : "Generate now"}
        </button>
        <label className="px-3 py-1.5 border border-nk-gold text-nk-gold text-xs font-medium rounded-sm inline-flex items-center gap-1.5 cursor-pointer hover:bg-nk-gold/10">
          <UploadSimple size={12} weight="bold" /> Upload file → AI extract
          <input ref={fileRef} type="file" hidden onChange={onUpload}
                 accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv"
                 data-testid="ai-upload-input" />
        </label>
        <button onClick={exportCSV} className="px-3 py-1.5 border border-nk-line text-xs text-white/60 hover:text-nk-text rounded-sm inline-flex items-center gap-1.5">
          ⬇ Export CSV
        </button>
        <button onClick={exportJSON} className="px-3 py-1.5 border border-nk-line text-xs text-white/60 hover:text-nk-text rounded-sm inline-flex items-center gap-1.5">
          ⬇ Export JSON
        </button>
        <button onClick={runDedupe} disabled={busy} data-testid="ai-dedupe-btn"
                className="px-3 py-1.5 border border-nk-warn text-nk-warn text-xs font-medium rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-nk-warn/10">
          🔎 AI find duplicates
        </button>
        <button onClick={() => setChampOpen(true)}
                className="px-3 py-1.5 border border-nk-gold text-nk-gold text-xs font-medium rounded-sm inline-flex items-center gap-1.5 hover:bg-nk-gold/10">
          👑 Championship batch
        </button>
        <span className="text-[10px] text-white/40">PDF · PNG · JPG · TXT · CSV up to 25MB</span>
      </div>

      {lastDone && (
        <p className="text-[10px] text-white/40 mt-3 inline-flex items-center gap-1">
          <CheckCircle size={10} weight="fill" className="text-nk-gold" />
          Last run · {lastDone.source} · {lastDone.inserted}/{lastDone.received} inserted · {new Date(lastDone.started_at).toLocaleString()}
        </p>
      )}

      {/* Steering panel: custom prompt + heatmap awareness */}
      <div className="mt-4 border-t border-nk-line pt-3">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          data-testid="ai-steering-toggle"
          className="text-[11px] text-white/60 uppercase tracking-wider hover:text-nk-text inline-flex items-center gap-1.5"
        >
          <Sparkle size={10} weight="bold" />
          {advancedOpen ? "▾ Hide AI steering" : "▸ AI steering (prompt + heatmap)"}
        </button>
        {advancedOpen && (
          <div className="mt-3 space-y-3" data-testid="ai-steering-panel">
            <div>
              <label className="text-[11px] text-white/60 uppercase tracking-wider">
                Custom prompt steering (optional)
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder='e.g. "Focus on Nollywood actors from the 2000s and Ghanaian highlife musicians."'
                data-testid="ai-custom-prompt"
                className="mt-1 w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none"
              />
              <p className="text-[10px] text-white/40 mt-1">{customPrompt.length}/500</p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useHeatmap}
                onChange={(e) => setUseHeatmap(e.target.checked)}
                data-testid="ai-use-heatmap"
                className="mt-0.5 accent-nk-gold"
              />
              <div>
                <p className="text-sm text-nk-text">
                  Use heatmap — bias generation toward under-covered categories
                </p>
                {heatmap && (
                  <p className="text-[10px] text-white/40 mt-0.5" data-testid="ai-heatmap-summary">
                    {heatmap.weak_buckets} thin bucket(s) of {heatmap.total_buckets} ·
                    {" "}{heatmap.in_cooldown_count} in cooldown · min fresh ≥ {heatmap.min_fresh}
                  </p>
                )}
              </div>
            </label>
            {useHeatmap && heatmap?.weak?.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="ai-heatmap-weak-list">
                {heatmap.weak.slice(0, 8).map((b, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-2 py-0.5 rounded-sm bg-nk-warn/10 border border-nk-warn/30 text-nk-warn font-mono"
                  >
                    {b.category}/{b.difficulty}/{b.country_tag}: {b.fresh}
                  </span>
                ))}
                {heatmap.weak.length > 8 && (
                  <span className="text-[10px] text-white/40">+{heatmap.weak.length - 8} more</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {champOpen && <ChampionshipModal onClose={() => setChampOpen(false)} onDone={() => { onChange?.(); refresh(); }} />}
      {dedupe && <DedupeModal data={dedupe} onClose={() => setDedupe(null)} onApplied={() => { onChange?.(); refresh(); setDedupe(null); }} />}
    </section>
  );
}

function ChampionshipModal({ onClose, onDone }) {
  const [count, setCount] = useState(15);
  const [extraPrompt, setExtraPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/questions/ai/championship-generate",
        { count, custom_prompt: extraPrompt || null });
      toast.success(`Championship batch: ${data.inserted}/${data.received} elite questions added`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="eyebrow text-nk-gold">Championship bank</p>
            <h2 className="font-display text-xl font-medium">Generate elite questions</h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-nk-text">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-white/60 mb-4">
          50% global elite · 50% Pan-African elite · 75% hard / 25% medium · only Championship draws from this bank.
        </p>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-white/60 uppercase tracking-wider">How many?</label>
            <input type="number" min={1} max={50} value={count} onChange={(e) => setCount(parseInt(e.target.value) || 15)}
                   className="w-16 bg-nk-surface border border-nk-line px-2 py-1 text-sm rounded-sm" />
          </div>
          <div>
            <label className="text-[11px] text-white/60 uppercase tracking-wider">Extra steering (optional)</label>
            <textarea value={extraPrompt} onChange={(e) => setExtraPrompt(e.target.value)} rows={3} maxLength={500}
                      placeholder='e.g. "Focus on Nigerian Premier League history and the African Cup of Nations finals."'
                      className="mt-1 w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
          </div>
          <button onClick={run} disabled={busy} data-testid="champ-generate-btn"
                  className="w-full px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm disabled:opacity-40">
            {busy ? "Generating…" : `Generate ${count} elite questions`}
          </button>
        </div>
      </div>
    </div>
  );
}

function DedupeModal({ data, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);

  const apply = async (group, keepQid) => {
    if (busy) return;
    if (!confirm(`Mark ${group.length - 1} duplicates as rejected (keeping ${keepQid})?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/questions/ai/dedupe-apply", {
        keep_qid: keepQid,
        drop_qids: group.filter((q) => q !== keepQid),
      });
      toast.success("Duplicates dropped");
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="eyebrow text-nk-warn">AI Dedupe</p>
            <h2 className="font-display text-xl font-medium">
              {(data.groups || []).length} duplicate group(s) across {data.scanned} questions
            </h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-nk-text"><X size={18} /></button>
        </div>
        {(data.groups || []).length === 0 ? (
          <p className="text-sm text-white/40">No duplicates found — your bank is clean.</p>
        ) : (
          <div className="space-y-3">
            {data.groups.map((g, i) => (
              <DedupeGroup key={i} group={g} onApply={(keep) => apply(g, keep)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DedupeGroup({ group, onApply }) {
  const [details, setDetails] = useState({});
  useEffect(() => {
    api.get("/admin/questions", { params: { search: group[0] } })
      .then(({ data }) => {
        const map = {};
        (data.items || []).forEach((d) => { map[d.qid] = d; });
        setDetails(map);
      })
      .catch(() => {});
  }, [group]);
  return (
    <div className="nk-card p-3">
      <p className="text-[11px] text-white/60 uppercase tracking-wider mb-2">{group.length} potential duplicates</p>
      <div className="space-y-1.5">
        {group.map((qid) => {
          const q = details[qid];
          return (
            <div key={qid} className="flex items-start gap-2 text-xs">
              <button onClick={() => onApply(qid)}
                      className="px-2 py-0.5 border border-nk-gold text-nk-gold hover:bg-nk-gold/10 rounded-sm shrink-0">
                Keep this
              </button>
              <div className="min-w-0">
                <p className="text-nk-text break-words">{q?.body || `(${qid})`}</p>
                <p className="text-white/40 font-mono text-[10px]">{qid}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ---------------- Taxonomy Strip (Iter28A) ----------------
function TaxonomyStrip() {
  const [mix, setMix] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/questions/taxonomy/mix"); setMix(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const retag = async (overwrite) => {
    if (busy) return;
    if (overwrite && !confirm("Overwrite ALL existing taxonomy tags? This rewrites scope, region, championship_tier on every question.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/questions/taxonomy/auto-tag?overwrite=${overwrite}`);
      toast.success(`Tagged ${data.updated} / ${data.scanned} questions`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!mix) return null;
  const target = mix.scope_target_pct || {};
  const actual = mix.scope_actual_pct || {};
  const total = mix.total_approved || 0;

  return (
    <section className="nk-card p-4 border-l-2 border-nk-gold" data-testid="taxonomy-strip">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="eyebrow text-nk-gold">Pan-African Taxonomy</p>
          <p className="text-sm text-white/60 mt-0.5">
            Every question gets a <span className="text-nk-text">scope</span>, <span className="text-nk-text">region</span>, and <span className="text-nk-text">championship tier</span>. Target mix: 65% universal · 22% African · 13% country-specific.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => retag(false)} disabled={busy}
                  data-testid="taxonomy-fill-missing"
                  className="px-3 py-1.5 border border-nk-line text-xs rounded-sm hover:border-nk-gold hover:text-nk-gold transition-colors disabled:opacity-40">
            Fill missing tags
          </button>
          <button onClick={() => retag(true)} disabled={busy}
                  data-testid="taxonomy-retag-all"
                  className="px-3 py-1.5 bg-nk-gold text-nk-bg text-xs font-medium rounded-sm hover:bg-nk-goldSoft transition-colors disabled:opacity-40">
            {busy ? "Working…" : "Retag all"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        {["universal", "african", "country-specific"].map((k) => {
          const tgt = target[k] || 0;
          const act = actual[k] || 0;
          const ok = Math.abs(act - tgt) <= 7;
          return (
            <div key={k} className="border border-nk-line rounded-sm p-3" data-testid={`taxonomy-scope-${k}`}>
              <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">{k}</p>
              <div className="flex items-baseline gap-2">
                <span className={`font-mono text-lg ${ok ? "text-nk-good" : "text-nk-warn"}`}>{act}%</span>
                <span className="text-xs text-white/40">target {tgt}%</span>
              </div>
              <div className="mt-2 h-1 bg-nk-line/40 rounded-sm overflow-hidden">
                <div className={`h-full ${ok ? "bg-nk-good" : "bg-nk-warn"}`} style={{ width: `${Math.min(100, act)}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] text-white/60">
        <div>
          <p className="text-white/40 mb-1">Regions</p>
          {Object.entries(mix.region_breakdown || {}).map(([k, v]) => (
            <span key={k} className="mr-2"><span className="text-nk-text">{k}:</span> {v}</span>
          ))}
        </div>
        <div>
          <p className="text-white/40 mb-1">Tiers</p>
          {Object.entries(mix.tier_breakdown || {}).map(([k, v]) => (
            <span key={k} className="mr-2"><span className="text-nk-text">{k}:</span> {v}</span>
          ))}
        </div>
        <div className="text-right">
          <p className="text-white/40">Total approved</p>
          <p className="font-mono text-nk-text text-base">{total}</p>
        </div>
      </div>
    </section>
  );
}

/**
 * AutoTagAfricanBtn — super-admin only. Runs the backend heuristic that
 * scans `ALL`-tagged questions and re-tags them with a specific African
 * country_tag when their body/category unambiguously names one country.
 *
 * Two-step UX: dry-run → confirm dialog with per-country counts + total →
 * apply. Idempotent because subsequent runs only touch remaining ALL rows.
 */
function AutoTagAfricanBtn({ onDone }) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data: dry } = await api.post("/admin/questions/auto-tag-african?dry_run=true");
      const total = dry.total_would_be_tagged || 0;
      if (total === 0) {
        toast.info("No new African-country matches found. Question bank is already tagged.");
        return;
      }
      const byCountry = Object.entries(dry.tagged_by_country || {})
        .sort(([, a], [, b]) => b - a)
        .map(([code, n]) => `  ${code}: ${n}`)
        .join("\n");
      const ok = window.confirm(
        `Auto-tag ${total} questions with an African country_tag?\n\n` +
        `Scanned:            ${dry.total_scanned}\n` +
        `Will tag:           ${total}\n` +
        `Ambiguous (skip):   ${dry.skipped_ambiguous || 0}\n\n` +
        `Per country:\n${byCountry}\n\n` +
        `NK Daily's 8/15 African mix will draw from this expanded pool.`
      );
      if (!ok) return;
      const { data: applied } = await api.post("/admin/questions/auto-tag-african?dry_run=false");
      toast.success(`Tagged ${applied.total_would_be_tagged} questions with African country codes`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-tag failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="questions-auto-tag-african-btn"
      title="Scan ALL-tagged questions and re-tag those that mention a specific African country by name."
      className="px-3 py-2 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 text-sm rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50"
    >
      <GlobeHemisphereWest size={14} weight="fill" />
      {busy ? "Scanning…" : "Auto-tag African"}
    </button>
  );
}

